import { useState } from 'react'
import { Layers, Shield, User } from 'lucide-react'
import { lineupLimitsLabel, REPEAT_KINDS, repeatChipText, repeatSubjectKey } from './prepackRepeatsModel'

const KIND_ICONS = { player: User, team: Shield, market: Layers }
// Chips shown before "Show all": a full surface can repeat dozens of subjects.
const COLLAPSED_CHIPS = 12

/**
 * The Surfaces page's "Repeats" strip (#861, #990): every player, team and
 * market shape in two or more of the page's packs (the lineup view's
 * `repeats`, counted from the same read as the cards), fullest first, with
 * how many the lineup shows and how many the limits hold back. The lineup
 * never shows more than a limit, so a subject is flagged for the packs it
 * holds back, never as over. `heldPacks` counts the packs held back, `limits`
 * are the ones the lineup is held to. Selecting a chip shows only its packs
 * (`onToggle(key)`); selecting it again shows them all (`onToggle(null)`).
 * Nothing when no subject repeats.
 */
export function SurfaceRepeatsStrip({ subjects = [], limits = null, heldPacks = 0, active = null, onToggle }) {
  const [expanded, setExpanded] = useState(false)
  if (subjects.length === 0) return null
  const limitsText = lineupLimitsLabel(limits)
  const activeIndex = subjects.findIndex((subject) => repeatSubjectKey(subject) === active)
  // The chosen chip stays in view when the strip is collapsed.
  const shown = expanded || subjects.length <= COLLAPSED_CHIPS
    ? subjects
    : subjects.filter((_, index) => index < COLLAPSED_CHIPS || index === activeIndex)
  const hidden = subjects.length - shown.length

  return (
    <section aria-label="Repeats" className="mb-4 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] px-3 py-2.5 text-xs">
      <p className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-semibold text-[var(--wf-color-text)]">Repeats</span>
        <span className={heldPacks ? 'font-semibold text-[var(--wf-color-warning)]' : 'text-[var(--prepacks-muted)]'}>
          {heldPacks ? `${heldPacks} pack${heldPacks === 1 ? '' : 's'} held back by the limits` : 'None held back'}
        </span>
        <span className="text-[var(--prepacks-muted)]">{limitsText ? `At most ${limitsText}` : 'No repeat limits set'}</span>
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {shown.map((subject) => {
          const key = repeatSubjectKey(subject)
          const pressed = active === key
          const Icon = KIND_ICONS[subject.kind] || Layers
          const count = repeatChipText(subject)
          const tone = pressed
            ? 'border-[var(--prepacks-accent-text)] bg-[var(--wf-color-action-soft)] text-[var(--prepacks-accent-text)]'
            : subject.held > 0
              ? 'border-[var(--wf-color-warning-border)] bg-[var(--wf-color-warning-soft)] text-[var(--wf-color-warning)]'
              : 'border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] text-[var(--prepacks-text)] hover:border-[var(--prepacks-muted)]'
          return (
            <li key={key} className="min-w-0 max-w-full">
              {/* The name says the kind the icon shows, then the chip's own words. */}
              <button
                type="button"
                aria-pressed={pressed}
                aria-label={`${REPEAT_KINDS[subject.kind]?.word || subject.kind}: ${subject.label}${count}`}
                onClick={() => onToggle(pressed ? null : key)}
                className={`flex min-w-0 max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-left transition-colors ${tone}`}
              >
                <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
                <span className="flex min-w-0">
                  <span className="min-w-0 truncate" title={subject.label}>{subject.label}</span>
                  <span className="shrink-0 whitespace-pre">{count}</span>
                </span>
              </button>
            </li>
          )
        })}
        {hidden > 0 || (expanded && subjects.length > COLLAPSED_CHIPS) ? (
          <li>
            <button type="button" onClick={() => setExpanded(!expanded)} className="rounded-full px-2 py-0.5 text-[var(--prepacks-accent-text)] hover:underline">
              {expanded ? 'Show fewer' : `Show ${hidden} more`}
            </button>
          </li>
        ) : null}
      </ul>
    </section>
  )
}
